import { useState, useCallback, useEffect } from 'react';

import { Member } from '../types/member.types';
import { membersService, SearchMembersParams } from '../services/members.service';
import { mapStaffsToMembers, mapSearchParamsToApiParams } from '../mappers/member-mappers';
import { useMemberSearchStore } from '../stores/member-search.store';
import { clearAllStaffCaches } from '../utils/staff-cache.utils';

import { MESSAGES } from '@/lib/constants';
import { useSnackbar } from '@/hooks/use-snackbar';
import { useConfirmDialog } from '@/hooks/use-confirm-dialog';

export const useMembers = () => {
  const [selectedMembers, setSelectedMembers] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [members, setMembers] = useState<Member[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const { setConfirmDialog } = useConfirmDialog();
  const { setSnackbarMessage } = useSnackbar();

  // Get state from global store
  const {
    searchInputValues,
    committedSearchParams,
    currentPage,
    itemsPerPage,
    sortField: storeSortField,
    sortOrder,
    setSearchInputValues,
    setCommittedSearchParams,
    setCurrentPage,
    setItemsPerPage,
    setSortField: setStoreSortField,
    setSortOrder,
    clearSearch,
  } = useMemberSearchStore();

  // Convert store sortField to proper type
  const sortField = (storeSortField || 'updatedAt') as 'createdAt' | 'updatedAt';
  const sortDirection = sortOrder; // Load members from API
  const loadMembers = useCallback(
    async (
      searchParams: Record<string, unknown> = {},
      page: number = 1,
      perPage: number = 100,
      sort: string | null = 'updatedAt',
      orderBy: 'asc' | 'desc' = 'desc'
    ) => {
      try {
        setLoading(true);
        // Convert frontend search params to API params
        const apiSearchParams = mapSearchParamsToApiParams(searchParams);
        const apiParams: SearchMembersParams = {
          ...apiSearchParams,
          page,
          perPage,
          sort: (sort as 'createdAt' | 'updatedAt') || 'createdAt',
          orderBy,
        };

        const response = await membersService.searchMembers(apiParams);
        const transformedMembers = mapStaffsToMembers(response.data);

        // Always create a new array reference to ensure React detects the change
        setMembers([...transformedMembers]);
        setTotalItems(response.pagination?.totalItems || 0);
      } catch (error) {
        console.error('Error loading members:', error);
        setMembers([]);
        setTotalItems(0);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    void loadMembers(committedSearchParams, currentPage, itemsPerPage, sortField, sortDirection);
  }, [committedSearchParams, currentPage, itemsPerPage, sortField, sortDirection, loadMembers]);

  const handleSearch = useCallback(
    (params: Record<string, unknown>, preservePage = false) => {
      setCommittedSearchParams(params);
      setSearchInputValues(params);
      // Only reset to page 1 if it's a new search, preserve page if restoring
      if (!preservePage) {
        setCurrentPage(1);
      }
    },
    [setCommittedSearchParams, setSearchInputValues, setCurrentPage, currentPage]
  );

  const handleSearchChange = useCallback(
    (params: Record<string, unknown>) => {
      setSearchInputValues(params);
    },
    [setSearchInputValues]
  );

  const handleClear = useCallback(() => {
    clearSearch();
    setSelectedMembers([]);
  }, [clearSearch]);

  const handlePageChange = useCallback(
    (page: number) => {
      setCurrentPage(page);
    },
    [setCurrentPage]
  );

  const handleItemsPerPageChange = useCallback(
    (itemsPerPage: number) => {
      setItemsPerPage(itemsPerPage);
      setCurrentPage(1);
    },
    [setItemsPerPage, setCurrentPage]
  );

  const handleSelectionChange = useCallback((selectedIds: string[]) => {
    setSelectedMembers(selectedIds);
  }, []);

  const handleSortChange = useCallback(
    (field: string, order: 'asc' | 'desc') => {
      const nextField = field as 'createdAt' | 'updatedAt';
      if (nextField !== sortField || order !== sortDirection) {
        setStoreSortField(nextField);
        setSortOrder(order);
        setCurrentPage(1);
      }
    },
    [sortField, sortDirection, setStoreSortField, setSortOrder, setCurrentPage]
  );

  const handleDelete = useCallback(
    async (memberIds: string[]) => {
      setLoading(true);
      try {
        await membersService.deleteMembers(memberIds);

        // Force fresh staff data across screens after bulk delete.
        clearAllStaffCaches();

        void loadMembers(
          committedSearchParams,
          currentPage,
          itemsPerPage,
          sortField,
          sortDirection
        );
        setSelectedMembers([]);
        setSnackbarMessage(MESSAGES.DELETE_STAFF_SUCCESS);
      } catch (error) {
        setConfirmDialog({
          title: (error as Error).message,
        });
      } finally {
        setLoading(false);
      }
    },
    [loadMembers, committedSearchParams, currentPage, itemsPerPage, sortField, sortDirection]
  );

  const handleRegisterAutocompleteClear = useCallback((_clearFn: () => void) => {}, []);

  return {
    searchResults: members,
    loading,
    currentPage,
    itemsPerPage,
    totalItems,
    selectedMembers,
    searchParams: searchInputValues, // Return input values for form fields
    committedSearchParams,
    sortField,
    sortOrder: sortDirection,
    handleSearch,
    handleSearchChange,
    handleClear,
    handlePageChange,
    handleItemsPerPageChange,
    handleSelectionChange,
    handleSortChange,
    handleDelete,
    handleRegisterAutocompleteClear,
  };
};
